// Cloud save: progress follows the account. The Worker stores one base64 blob per player
// (plus a one-generation backup) and refuses blind overwrites: an upload names the version it
// replaces (baseUploadedAtMs) and gets 409 if another device uploaded in between.
import { api, ApiError } from './api';
import type { Accounts } from './auth';

/** Who the save in this browser belongs to: a player id, or a guest. */
export type SaveOwner = string | 'guest';

export interface CloudMeta { exists: boolean; savedAtMs: number; uploadedAtMs: number; level: number }
interface CloudGet extends CloudMeta { blob?: string }

export type BootChoice =
  /** Keep this browser's save (and upload it as the account's). */
  | 'local'
  /** Replace this browser's save with the account's. */
  | 'cloud'
  /** A guest's progress meets an account that already has progress: the player chooses. */
  | 'ask'
  /** Start fresh: this browser holds another account's save and this account has none. */
  | 'fresh';

export interface BootInput {
  uid: string;
  local: { owner: SaveOwner; lastSeen: number; bestStage: number } | null;
  cloud: CloudMeta;
}

/**
 * Which save a signed-in player boots into. Nothing is ever deleted: the losing copy is kept
 * aside by the caller. A guest with barely any progress doesn't get asked.
 */
export function decideBootSave({ uid, local, cloud }: BootInput): BootChoice {
  if (!local) return cloud.exists ? 'cloud' : 'local';
  if (local.owner === uid) return cloud.exists && cloud.savedAtMs > local.lastSeen ? 'cloud' : 'local';
  if (local.owner === 'guest') {
    if (!cloud.exists) return 'local';
    return local.bestStage > 1 ? 'ask' : 'cloud';
  }
  return cloud.exists ? 'cloud' : 'fresh';
}

const OWNER_KEY = 'tl.save.owner';
const baseKey = (uid: string) => `tl.cloud.base.${uid}`;

function readLocal(key: string): string | null { try { return localStorage.getItem(key); } catch { return null; } }
function writeLocal(key: string, v: string | null): void {
  try { if (v === null) localStorage.removeItem(key); else localStorage.setItem(key, v); } catch { /* storage blocked */ }
}

export function saveOwner(): SaveOwner | null { return readLocal(OWNER_KEY); }
export function setSaveOwner(owner: SaveOwner | null): void { writeLocal(OWNER_KEY, owner); }

/** UTF-8 JSON <-> base64, the blob format the Worker accepts. */
export function encodeBlob(json: string): string {
  const bytes = new TextEncoder().encode(json);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}
export function decodeBlob(b64: string): string {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

const UPLOAD_EVERY_MS = 30_000;

export class CloudSave {
  /** False after a failed boot download: uploading blind could overwrite the account's real progress. */
  private enabled = false;
  private dirty = false;
  private uploading: Promise<boolean> | null = null;

  constructor(private accounts: Accounts, private snapshot: () => { json: string; level: number; version: number }) {
    setInterval(() => { if (this.dirty) void this.flush(); }, UPLOAD_EVERY_MS);
  }

  /** Downloads the account's save at boot. Null means "couldn't reach it": play on locally, don't upload. */
  async download(): Promise<(CloudMeta & { json?: string }) | null> {
    const uid = this.accounts.user?.uid;
    if (!uid) return null;
    try {
      const r = await api<CloudGet>('/v1/save', { token: this.accounts.token });
      const meta: CloudMeta = { exists: !!r.exists, savedAtMs: Number(r.savedAtMs ?? 0), uploadedAtMs: Number(r.uploadedAtMs ?? 0), level: Number(r.level ?? 0) };
      writeLocal(baseKey(uid), String(meta.uploadedAtMs));
      this.enabled = true;
      return { ...meta, json: r.exists && r.blob ? decodeBlob(r.blob) : undefined };
    } catch (e) {
      console.warn('Cloud save unavailable this session:', e);
      return null;
    }
  }

  /** Remembers there's something to upload; the periodic flush picks it up. */
  markDirty(): void { if (this.enabled) this.dirty = true; }

  /**
   * Uploads now. Resolves true only once the server holds this exact state, which is what a
   * purchase grant waits for before it's acknowledged.
   */
  flush(): Promise<boolean> {
    if (!this.enabled || !this.accounts.user) return Promise.resolve(false);
    if (this.uploading) return this.uploading.then(() => this.flush());
    this.dirty = false;
    this.uploading = this.upload().finally(() => { this.uploading = null; });
    return this.uploading;
  }

  private async upload(retried = false): Promise<boolean> {
    const uid = this.accounts.user?.uid;
    if (!uid) return false;
    const { json, level, version } = this.snapshot();
    try {
      const r = await api<{ uploadedAtMs: number }>('/v1/save', {
        body: {
          blob: encodeBlob(json), savedAtMs: Date.now(), level, ver: String(version),
          baseUploadedAtMs: Number(readLocal(baseKey(uid)) ?? 0),
        },
        token: this.accounts.token,
      });
      writeLocal(baseKey(uid), String(r.uploadedAtMs));
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'conflict' && !retried) {
        // Another device uploaded since we loaded. This device is the one being played now, so it
        // wins; the server keeps the replaced copy as its one-generation backup.
        const current = (e.body.current ?? {}) as { uploadedAtMs?: number };
        console.warn('Cloud save changed on another device; replacing it with this one.');
        writeLocal(baseKey(uid), String(Number(current.uploadedAtMs ?? 0)));
        return this.upload(true);
      }
      console.warn('Cloud save upload failed:', e);
      this.dirty = true;
      return false;
    }
  }

  /** Stops uploading (sign-out). */
  disable(): void { this.enabled = false; this.dirty = false; }
}
